import * as React from "react";
import {ReactElement} from "react";
import {useStore, useStoreValue, useSetStoreValue} from "react-context-hook";
import Icon from "../Icon";
import {getConfiguration, getText} from "../../services/configuration.service";
import {useEffect} from "react";
import BookingCalendar from "../BookingCalendar";
import {getDurationLabel, parseDate} from "../../services/label.service";
import {
    IAvailability,
    IBookingCalendarMonthYear,
    IBookingCalendarSearchCity
} from "../index";

interface BasePriceDayCodeType {
    byHousing: boolean
    categories: string
    day: string
    dayNb: number
    departureCity: string
    departureDate: number
    departureLabel: string
    departureValue: string
    maxPax: number
    minPax: number
    nightNb: number
    price: number
    reduction: number
    reductionRate: number
    regularPrice: number
    returnLabel: string
    returnValue: string
    status: string
}

const SelectDepartureDate = (): ReactElement => {
    const bookingEngineData = useStoreValue("bookingEngineData");
    const basePriceAvailability = ((bookingEngineData || {}).product || {}).basePriceAvailability;
    const isLocationContext = bookingEngineData?.product?.location;
    const availabilities = (bookingEngineData || {}).availabilities;
    const cities = (bookingEngineData || {}).cities;
    const search = (bookingEngineData || {}).search;
    const selectedDepartureCity = useStoreValue("selectedDepartureCity");
    const selectedDuration = useStoreValue("selectedDuration");
    const selectedCapacity = useStoreValue("selectedCapacity");
    const setChooseCalendarDefaultDate = useSetStoreValue("calendarDefaultValue");
    const [selectedDepartureDate, setSelectedDepartureDate] = useStore("selectedDepartureDate");
    const selectedDepartureCityLabel = (cities && selectedDepartureCity && cities[selectedDepartureCity.code]) || getText("booking.loading.label");

    useEffect(() => {
        const selectedCityIsBaseAvail = ((basePriceAvailability || {}).departureCity || {}).code === (selectedDepartureCity || {}).code;
        const selectedDurationIsBaseAvail = ((basePriceAvailability || {}).nbDays + "-" + (basePriceAvailability || {}).nbNights) === (selectedDuration || {}).code;
        const selectedDataIsBaseAvail = selectedCityIsBaseAvail && selectedDurationIsBaseAvail;

        if (!selectedDepartureDate || selectedDataIsBaseAvail) {
            if (bookingEngineData && basePriceAvailability) {
                // select from hash url
                const queryParameters = getConfiguration().queryParameters;
                let cityCode = basePriceAvailability.departureCity.code;
                if (queryParameters && queryParameters.city && availabilities[queryParameters.city]) {
                    cityCode = queryParameters.city;
                }
                let durationCode = basePriceAvailability.nbDays + "-" + basePriceAvailability.nbNights;
                const capacityCode = selectedCapacity ? selectedCapacity.code : (basePriceAvailability.minPax + "-" + basePriceAvailability.maxPax);
                if (queryParameters && queryParameters.duration && availabilities[cityCode][queryParameters.duration]) {
                    durationCode = queryParameters.duration;
                }
                let [basePriceMonthCode, basePriceDayCode] = parseDate(basePriceAvailability.departureDate);
                if (queryParameters && queryParameters.departureDate) {
                    [basePriceMonthCode, basePriceDayCode] = parseDate(queryParameters.departureDate);
                }

                const selectedDuration: any = isLocationContext ? availabilities[cityCode][capacityCode][durationCode] : availabilities[cityCode][durationCode];
                const selectedMonth = selectedDuration[basePriceMonthCode];
                const selectedDay = getDay(basePriceDayCode, selectedMonth, selectedDuration);
                setSelectedDepartureDate({
                    code: selectedDay?.departureValue ?? basePriceAvailability.departureDate,
                    value: selectedDay
                });
            }
        } else if (selectedDepartureDate) {
            const selectedDurationAvail = selectedDuration && (isLocationContext ? selectedDepartureCity.value[selectedCapacity.code][selectedDuration.code] :
                selectedDepartureCity.value[selectedDuration.code]);
            let selectedDepartureDateCode;
            if (selectedDurationAvail) {
                // select from hash url
                const queryParameters = getConfiguration().queryParameters;
                if (queryParameters && queryParameters.departureDate) {
                    const [basePriceMonthCode, basePriceDayCode] = parseDate(queryParameters.departureDate);
                    const selectedMonth = selectedDurationAvail[basePriceMonthCode];
                    if (selectedMonth) {
                        const selectedDay = selectedMonth[basePriceDayCode];
                        if (selectedDay) {
                            selectedDepartureDateCode = queryParameters.departureDate;
                        }
                    }

                }
                if (!selectedDepartureDateCode) {
                    const departureDateLowestPrice: IAvailability = isLocationContext ? getLowestPriceFromDurations(selectedDurationAvail) :
                        getDepartureDateLowestPrice(selectedDurationAvail);
                    if (departureDateLowestPrice && departureDateLowestPrice.departureValue) {
                        selectedDepartureDateCode = departureDateLowestPrice.departureValue;
                    } else {
                        const selectedDepartureMonth = selectedDurationAvail[Object.keys(selectedDurationAvail)[0]];
                        if (selectedDepartureMonth) {
                            const selectedDepartureDay = selectedDepartureMonth[Object.keys(selectedDepartureMonth)[0]];
                            if (selectedDepartureDay) {
                                selectedDepartureDateCode = selectedDepartureDay.departureValue;
                            }
                        }
                    }
                }
            }
            if (selectedDepartureDateCode) {
                const [selectedMonth, selectedDay] = parseDate(selectedDepartureDateCode);
                const duration = isLocationContext ? selectedDepartureCity.value[selectedCapacity.code][selectedDuration.code][selectedMonth][selectedDay]
                    :selectedDepartureCity.value[selectedDuration.code][selectedMonth][selectedDay];
                setSelectedDepartureDate({
                    code: selectedDepartureDateCode,
                    value: duration
                });
            }
        }
    }, [bookingEngineData, selectedDepartureCity, selectedDuration]);

    const selectBestPriceAvailability= (selectedMonth) => {
        if (!isLocationContext) {
            setChooseCalendarDefaultDate(true);
        }
        const newSelectedDay = Object.values(selectedMonth).sort((a: BasePriceDayCodeType, b: BasePriceDayCodeType) => {
            if (a.price === b.price) {
                return a.day < b.day ? -1 : 1;
            }
            return a.price < b.price ? -1 : +1;
        });
        return newSelectedDay[0];
    };

    const getDay = (basePriceDayCode, selectedMonth, selectedDuration) => {
        if (selectedMonth) {
            return selectedMonth[basePriceDayCode] !== undefined ? selectedMonth[basePriceDayCode]: selectBestPriceAvailability(selectedMonth);
        }
        // choose the cheapest day of the selected duration
        return getLowestPriceFromDurations(selectedDuration);

    } ;

    const getDepartureDateLowestPrice = (selectedDurationAvail: IBookingCalendarMonthYear) => {
        const selectedDepartureCityData: IBookingCalendarSearchCity = search.departureCity.filter((departureCity: any) => departureCity.code === selectedDepartureCity.code)[0];
        const lowestDurationPrice = selectedDepartureCityData && selectedDepartureCityData.lowestPrice;
        let lowestAvailDay;
        if (selectedDurationAvail && lowestDurationPrice) {
            Object.keys(selectedDurationAvail).forEach((durationAvail: string) => {
                const selectedDurationAvailData = selectedDurationAvail[durationAvail];
                Object.keys(selectedDurationAvailData).forEach((dayAvail: string) => {
                    const selectedDayAvailData = selectedDurationAvailData[dayAvail];
                    if (selectedDayAvailData.price === lowestDurationPrice) {
                        lowestAvailDay = selectedDayAvailData;
                    }
                });
            });
            return lowestAvailDay && lowestAvailDay;
        }
    };

    const getLowestPriceFromDurations = (selectedDuration: IBookingCalendarMonthYear) => {
        const defaultMonth = Object.keys(selectedDuration)[0];
        let cheapestDay = Object.values(selectedDuration[defaultMonth])[0];
        Object.values(selectedDuration).map(duration => {
            const days = Object.values(duration);
            days.forEach(day => {
                if (day.price < cheapestDay["price"]) {
                    cheapestDay = day;
                }
            });
        });
        return cheapestDay;
    };

    return (
        <div className="booking-item js-switch-active">
            <div className="label">{getText("booking.select.departure.date.label")}</div>
            <div className="elem-custom-select">
                <div className="custom-select-mask">
                    <div className="custom-select-input">
                        <div className="custom-select-input-txt">
                            <Icon icon={"C"} addClass={"calendar-icon"}/>
                            <div className="label-style">{((selectedDepartureDate || {}).value || {}).departureLabel || getText("booking.loading.label")}</div>
                        </div>
                        <div className="custom-select-input-button">{getText("booking.select.change")}</div>
                    </div>
                </div>
                <div className="custom-select-panel d-none">
                    <div className="title-block">
                        <div className="label-block">
                            <div className="label">{getText("booking.select.departure.date.label")}</div>
                            <div className="legend">
                                <span className="option">
                                    {getText("booking.select.departure.from.city")}&nbsp;
                                    <strong>{selectedDepartureCityLabel}</strong> | {getText("booking.select.duration.label")} :&nbsp;
                                    <strong>{(selectedDuration && getDurationLabel(selectedDuration.code)) || getText("booking.loading.label")}</strong>
                                </span>
                            </div>
                        </div>
                        <div className="icon-wrap btn-close-custom-select">
                            <Icon icon={"x"}/>
                        </div>
                    </div>
                    <div className="custom-select-panel-content">
                        <BookingCalendar/>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelectDepartureDate;
